import React from 'react';

import Button from '@atlaskit/button/standard-button';
import MoreIcon from '@atlaskit/icon/glyph/more';

import {
	AppRecommendationsAdControl,
	AppRecommendationsProvider,
} from '@atlassian/app-recommendations-kit';

import {
	useInvitePeopleModal,
	InvitePeopleModalContextProvider,
} from '@atlassian/invite-people/InvitePeopleModalContext';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl } from '@atlassian/jira-intl';

import {
	JiraYourWorkNextBestActionMessage,
	type JiraYourWorkNextBestActionMessageProps,
} from './crossflow-message';
import { ErrorBoundary } from './error-boundary';
import { type InviteUserMessageProps, InviteUserMessage } from './expansion-message';
import { useJiraYourWorkExperiment, JIRA_PRODUCT_DISCOVERY_PRODUCT_ID } from './use-experiment';
import {
	useFireButtonClickEvent,
	EXPERIMENT_ID,
	useFireUCIAButtonEvent,
} from './use-experiment/utils';

export type JiraYourWorkMailboxProps = JiraYourWorkNextBestActionMessageProps &
	InviteUserMessageProps &
	MessageConfigProps;

type MessageConfigProps = {
	showCrossflowMessage: boolean;
	showExpansionMessage: boolean;
	onDismissCrossflowMessage: () => void;
	onDismissExpansionMessage: () => void;
};

export const JiraYourWorkMailboxNative = ({
	AdControls = () => <></>,
	onTryNowClick,
	onLearnMoreClick,
	onInviteUserClick,
	showCrossflowMessage,
	showExpansionMessage,
	onDismissCrossflowMessage,
	onDismissExpansionMessage,
	isSiteAdmin,
}: JiraYourWorkMailboxProps) => (
	<>
		{showCrossflowMessage ? (
			<JiraYourWorkNextBestActionMessage
				AdControls={AdControls}
				onTryNowClick={onTryNowClick}
				onLearnMoreClick={onLearnMoreClick}
				onDismissCrossflowMessage={onDismissCrossflowMessage}
				isSiteAdmin={isSiteAdmin}
			/>
		) : null}
		{showExpansionMessage ? (
			<InviteUserMessage
				onInviteUserClick={onInviteUserClick}
				onDismissExpansionMessage={onDismissExpansionMessage}
			/>
		) : null}
	</>
);

export const JiraYourWorkMailbox = (props: JiraYourWorkMailboxProps) => (
	// We can run A/A Test and roll out the feature to 100% at the same time
	<>{fg('jira_your_work_recommendations_native') && <JiraYourWorkMailboxNative {...props} />}</>
);

const NbaExperimentCards = () => {
	const {
		cloudId,
		isSiteAdmin,
		openCrossFlow,
		isCrossflowMessageVisible,
		isExpansionMessageVisible,
		dimissExpansionMessage,
		dismissCrossflowMessage,
		contextResolver,
		cohort,
		onLearnMoreClick,
	} = useJiraYourWorkExperiment();

	const { openModal } = useInvitePeopleModal({
		resourceAri: `ari:cloud:jira::site/${cloudId}`,
		subProduct: 'product-discovery',
		enableThirdParty: true,
		source: EXPERIMENT_ID,
	});

	const fireTryNowAnalyticsEvent = useFireUCIAButtonEvent({ eventName: 'tryNow', cohort });
	const fireLearnMoreAnalyticsEvent = useFireUCIAButtonEvent({ eventName: 'learnMore', cohort });
	const fireAddTeamMembersAnalyticsEvent = useFireButtonClickEvent({
		eventName: 'addTeamMembers',
		cohort,
	});
	const fireReasonsAnalyticsEvent = useFireButtonClickEvent({
		eventName: 'adControlsReasons',
		cohort,
	});

	if (cohort === 'control' || cohort === 'not-enrolled') return null;

	const handleOnTryNowClicked = () => {
		fireTryNowAnalyticsEvent();
		return openCrossFlow();
	};

	const handleOnInviteUserClicked = () => {
		fireAddTeamMembersAnalyticsEvent();
		openModal();
	};

	const handleOnLearnMoreClicked = () => {
		fireLearnMoreAnalyticsEvent();
		return onLearnMoreClick();
	};

	return (
		<AppRecommendationsProvider
			contextResolver={contextResolver}
			onEvents={({ type }) => {
				if (type === 'DISMISS_STARTED') {
					dismissCrossflowMessage();
				} else if (type === 'REASONS_BUTTON_CLICKED') {
					fireReasonsAnalyticsEvent();
				}
			}}
		>
			<JiraYourWorkMailbox
				onDismissCrossflowMessage={dismissCrossflowMessage}
				onDismissExpansionMessage={dimissExpansionMessage}
				AdControls={() => (
					<AppRecommendationsAdControl
						productId={JIRA_PRODUCT_DISCOVERY_PRODUCT_ID}
						trigger={({ triggerRef, ...props }) => (
							<Button
								{...props}
								spacing="compact"
								appearance="subtle"
								iconBefore={<MoreIcon label="more" size="medium" />}
								ref={triggerRef}
							/>
						)}
						name="Jira Product Discovery"
						reasons={['the size of your team', 'your existing Atlassian products']}
					/>
				)}
				onInviteUserClick={handleOnInviteUserClicked}
				onLearnMoreClick={handleOnLearnMoreClicked}
				onTryNowClick={handleOnTryNowClicked}
				showCrossflowMessage={isCrossflowMessageVisible}
				showExpansionMessage={isExpansionMessageVisible}
				isSiteAdmin={isSiteAdmin}
			/>
		</AppRecommendationsProvider>
	);
};

const WrappedNbaExperimentCards = () => {
	const { locale } = useIntl();

	if (!locale.includes('en')) {
		return null;
	}

	return (
		<ErrorBoundary errorId="trailblazer.nbaexperiment.wrappednbaexperimentcards">
			<InvitePeopleModalContextProvider>
				<NbaExperimentCards />
			</InvitePeopleModalContextProvider>
		</ErrorBoundary>
	);
};

export default JiraYourWorkMailbox;
export { WrappedNbaExperimentCards as NbaExperimentCards };
